import { useNavigate, NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  CheckCircle2,
  MessageSquare,
  LogOut,
  PieChart,
  FolderOpen,
  History,
  ArrowLeftRight,
} from 'lucide-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { TicketButton } from '@/features/tickets/TicketButton';

const navItems = [
  { to: '/portal/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/portal/transacoes', label: 'Lançamentos', icon: ArrowLeftRight },
  { to: '/portal/dre', label: 'DRE', icon: PieChart },
  { to: '/portal/documentos', label: 'Documentos', icon: FolderOpen },
  { to: '/portal/timeline', label: 'Linha do tempo', icon: History },
  { to: '/portal/aprovacoes', label: 'Aprovações', icon: CheckCircle2 },
  { to: '/portal/relatorios', label: 'Relatórios', icon: FileText },
  { to: '/portal/mensagens', label: 'Mensagens', icon: MessageSquare },
];

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase();
}

export function PortalLayout() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen flex-col bg-secondary">
      {/* Topbar do portal */}
      <header className="flex items-center justify-between border-b border-border bg-white px-6 py-4">
        <img
          src="/identidade-visual/logo/vetor/logo-horizontal.svg"
          alt="Clinance"
          className="h-8 w-auto"
        />

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <TicketButton variant="topbar" clientId={profile?.client_id ?? null} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-secondary">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-xs font-bold text-white">
                    {getInitials(profile?.name || 'C')}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium text-foreground sm:block">
                  {profile?.name || 'Clínica'}
                </span>
                <ChevronDown size={14} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="text-destructive focus:text-destructive"
              >
                <LogOut size={14} className="mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Nav mobile */}
      <nav className="flex overflow-x-auto border-b border-border bg-white px-2 md:hidden">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-3 text-xs font-medium transition-colors ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`
            }
          >
            <Icon size={15} />
            {label}
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 p-6">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-white px-6 py-4 text-center text-xs text-muted-foreground">
        Portal do Cliente Clinance · Seus dados são protegidos pela{' '}
        <a href="#" className="underline hover:text-foreground">
          LGPD
        </a>
      </footer>
    </div>
  );
}
